import React from 'react';
import { AutoSizer } from 'react-virtualized';
import { useDispatch } from 'react-redux';
import { fetchDataSamples, updateFormData } from 'js/actions';
import { useIsLoading } from 'new/hooks/adapters/useIsLoading';
import { FORM_TYPE_CREATE_REUSABLE_TX } from 'js/constants/formTypes';
import FunctionArgsValues from 'new/components/ReusableTransform/FunctionArgsValues';
import LoadableMonacoEditor from 'js/components/elements/editors/monaco/LoadableMonacoEditor';
import { useDebouncedCallback } from 'new/hooks/useDebouncedCallback';
import { useDataSetsOptionsWithPreloading } from 'new/hooks/useDataSetsOptionsWithPreloading';
import { getDataSetSamplesFromResponse } from 'new/utils/DataSetUtil/getDataSetSamplesFromResponse';
import { generateInputFormDataFromDataSet } from 'new/utils/DataSetUtil/generateInputFormDataFromDataSet';
import { MonacoCodeEditorOnGetMarkers } from 'js/components/elements/editors/monaco/MonacoCodeEditor';
import { FORM_DID_UPDATE_STAGE_DATA, GET_DATA_SETS_REQUEST } from 'js/constants/actionTypes';

import './InputTestSample.scss';
import { SearchFilterViews } from 'js/constants/visibilityFilters';
import ResourceSelectionButton from 'new/elements/ResourceSelectionButton';
import { RESOURCE_TYPE_DATA_SET } from 'js/constants/resourceTypes';
import { ExpandView } from 'js/components/recipeBuilder/NexsetSampleView/components/ExpandView';

type Props = {
  transformId: any;
  onChangeMarkers: MonacoCodeEditorOnGetMarkers;
  inputAttrs: string[];
  isRecordType: boolean;
  inputCode: string;
  onChangeInputCode: (code: string) => void;
  argumentValues: Array<{ name: string; description: string }>;
  argumentsTestData: Array<{ fName: string; type: string; value: any }>;
};

function InputTestSample(props: Props) {
  const {
    inputAttrs = [],
    argumentValues = [],
    argumentsTestData = [],
    onChangeInputCode,
    onChangeMarkers,
  } = props;

  const view = SearchFilterViews.MY_DATA_SETS;
  const isLoading = useIsLoading(GET_DATA_SETS_REQUEST, FORM_DID_UPDATE_STAGE_DATA);

  const dispatch = useDispatch<any>();
  const onChangeInputCodeDebounced = useDebouncedCallback(
    onChangeInputCode,
    [onChangeInputCode],
    300,
  );
  const { options } = useDataSetsOptionsWithPreloading();

  const getSamplesFromAPI = async function (id: number) {
    if (!id) return null;
    const response = await dispatch(fetchDataSamples(id));
    return getDataSetSamplesFromResponse(response);
  };

  async function loadSample(nexsets) {
    const { id: dataSetID } = nexsets[0];
    const data = { selectedDataSetId: dataSetID };

    const samplesResponse = await getSamplesFromAPI(Number(dataSetID));
    await dispatch(updateFormData(FORM_TYPE_CREATE_REUSABLE_TX, data));

    let sample = samplesResponse[dataSetID];

    if (!sample) {
      const { samples } = await onClickPreviewData(dataSetID);
      sample = samples[dataSetID];
    }

    if (sample?.flat?.out) {
      const args = {
        id: dataSetID,
        samples: samplesResponse,
        isRecordType: props.isRecordType,
      };
      const formData = generateInputFormDataFromDataSet(args);
      dispatch(updateFormData(FORM_TYPE_CREATE_REUSABLE_TX, { ...formData }));
    }
  }

  const onClickPreviewData = async function (
    dataSetID: number,
  ): Promise<{ input: string; samples: object }> {
    if (!dataSetID) return { input: '', samples: [] };
    const samples = await getSamplesFromAPI(dataSetID);
    const args = { id: dataSetID, samples, isRecordType: props.isRecordType };
    const formData = generateInputFormDataFromDataSet(args);
    return {
      input: formData?.input || '',
      samples,
    };
  };

  const minHeight = argumentValues?.length >= 3 ? '80%' : '50%';

  return (
    <div className="input-base-container" style={{ minHeight }}>
      <div className="inner-container radius-container">
        {props.isRecordType ? null : (
          <>
            <FunctionArgsValues
              inputAttrs={inputAttrs}
              argumentValues={argumentValues}
              argumentsTestData={argumentsTestData}
            />

            <div className="divider" />
          </>
        )}
        <div className="test-sample-input">
          <div className="input-header">
            <span className="title">Test SAMPLE Input</span>
            <ResourceSelectionButton
              fullWidth={false}
              key={props.transformId}
              onChoose={loadSample}
              resourceType={RESOURCE_TYPE_DATA_SET}
              ExpandView={ExpandView}
              label="Load Sample from Nexset"
              hideSelection
              previewActionLabel="Preview Sample Data"
            />
          </div>
          <InputTestSampleCodeEditor
            loading={isLoading}
            code={props.inputCode}
            onChange={onChangeInputCodeDebounced}
            onChangeMarkers={onChangeMarkers}
          />
        </div>
      </div>
    </div>
  );
}

export default InputTestSample;

function InputTestSampleCodeEditor({ loading, code, onChange, onChangeMarkers }) {
  return (
    <div className="monaco-code-editor-container radius-container">
      {loading ? null : (
        <AutoSizer>
          {({ width, height }) => (
            <LoadableMonacoEditor
              height={height}
              width={width}
              theme="light"
              language="json"
              value={code}
              onChange={onChange}
              onChangeMarkers={onChangeMarkers}
            />
          )}
        </AutoSizer>
      )}
    </div>
  );
}
